'use client'

import React, { useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import { CalendarDays } from 'lucide-react'
import { toast } from 'react-toastify'
import { isUserAvailable } from 'helpers/authUser'
import { useAppSelector } from 'features/hooks'
import { RootState } from 'features/store'
import {
  FieldLabel,
  GreenPillButton,
  ModalCloseButton,
  ShoppingModalShell,
  TextCancelButton,
  TextField,
} from '@/components/shopping-lists/ShoppingOverlay'
import { formatMediumDate, formatTime, toDateKey } from '@/lib/mealPlanner/dates'
import { mealRecipeFromDraft } from '@/lib/mealPlanner/recipes'
import { addRecipes, createMeal, useMeals } from '@/lib/mealPlanner/store'
import type { PlannerRecipeDraft } from '@/lib/mealPlanner/types'

const fieldClass =
  'h-[50px] w-full rounded-lg border border-[#CBD7DD] bg-white px-4 text-base font-medium text-[#202020] outline-none focus:border-[#85C349]'

export function AddToMealPlanner({ recipe }: { recipe: PlannerRecipeDraft }) {
  const user = useAppSelector((state: RootState) => state.culineerAuth.user)
  const [open, setOpen] = useState(false)
  if (!isUserAvailable(user)) return null

  return (
    <>
      <div className="flex flex-col gap-3 rounded-2xl border border-[#E6E6E6] bg-white p-4">
        <div className="flex items-center gap-2">
          <CalendarDays size={20} className="text-[#2E7D0F]" />
          <h3 className="text-lg font-semibold leading-[22px] text-[#2E7D0F]">Add to meal planner</h3>
        </div>
        <p className="text-sm leading-5 text-[#6B6B6B]">
          Put this recipe on a meal. Ingredients stay off your shopping lists until you add them from the meal.
        </p>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex h-10 w-full items-center justify-center rounded-full bg-[#85C349] text-base font-semibold text-white hover:bg-[#6EAC36]"
        >
          Add to meal planner
        </button>
      </div>
      <AddToMealPlannerDialog recipe={recipe} open={open} onClose={() => setOpen(false)} />
    </>
  )
}

function AddToMealPlannerDialog({
  recipe,
  open,
  onClose,
}: {
  recipe: PlannerRecipeDraft
  open: boolean
  onClose: () => void
}) {
  const router = useRouter()
  const { meals } = useMeals()
  const [mealId, setMealId] = useState('new')
  const [name, setName] = useState(recipe.name)
  const [date, setDate] = useState(toDateKey(new Date()))
  const [time, setTime] = useState('')

  const upcoming = useMemo(
    () =>
      [...meals]
        .filter((meal) => meal.date >= toDateKey(new Date()))
        .sort((a, b) => a.date.localeCompare(b.date) || (a.time || '').localeCompare(b.time || '')),
    [meals]
  )

  const save = () => {
    const nextRecipe = mealRecipeFromDraft(recipe)
    if (mealId !== 'new') {
      const meal = meals.find((item) => item.id === mealId)
      if (!meal) return
      if (meal.recipes.some((item) => item.id === nextRecipe.id)) {
        toast.success(`${recipe.name} is already on ${meal.name}`)
        router.push(`/meal-planner/${meal.id}`)
        onClose()
        return
      }
      addRecipes(meal.id, [nextRecipe])
      toast.success(`Added ${recipe.name} to ${meal.name}`)
      router.push(`/meal-planner/${meal.id}`)
      onClose()
      return
    }
    if (!name.trim()) {
      toast.error('Give the meal a name')
      return
    }
    const created = createMeal({
      name,
      date,
      time,
      recipes: [nextRecipe],
    })
    if (!created) return
    toast.success(`Added ${recipe.name} to ${created.name}`)
    router.push(`/meal-planner/${created.id}`)
    onClose()
  }

  return (
    <ShoppingModalShell open={open} onClose={onClose} widthClass="w-full max-w-[534px]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-[#202020]">Add to meal planner</h2>
          <p className="mt-2 text-base text-[#4A4A4A]">{recipe.name}</p>
        </div>
        <ModalCloseButton onClick={onClose} />
      </div>
      <div className="mt-5 flex max-h-[240px] flex-col gap-2 overflow-y-auto">
        <button
          type="button"
          onClick={() => setMealId('new')}
          className={`rounded-xl border px-4 py-3 text-left font-semibold ${
            mealId === 'new' ? 'border-[#85C349] bg-[#F3FAEB] text-[#202020]' : 'border-[#E6E6E6] text-[#202020]'
          }`}
        >
          New meal
        </button>
        {upcoming.map((meal) => (
          <button
            key={meal.id}
            type="button"
            onClick={() => setMealId(meal.id)}
            className={`rounded-xl border px-4 py-3 text-left ${
              mealId === meal.id ? 'border-[#85C349] bg-[#F3FAEB]' : 'border-[#E6E6E6]'
            }`}
          >
            <span className="block font-semibold text-[#202020]">{meal.name}</span>
            <span className="text-sm text-[#6B6B6B]">
              {formatMediumDate(meal.date)}
              {meal.time ? ` · ${formatTime(meal.time)}` : ''}
            </span>
          </button>
        ))}
      </div>
      {mealId === 'new' && (
        <div className="mt-4 flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <FieldLabel>Meal name</FieldLabel>
            <TextField value={name} onChange={setName} placeholder="e.g. Family dinner" />
          </div>
          <div className="flex flex-col gap-2">
            <FieldLabel>Date</FieldLabel>
            <input className={fieldClass} type="date" value={date} onChange={(event) => setDate(event.target.value)} />
          </div>
          <div className="flex flex-col gap-2">
            <FieldLabel>Time (optional)</FieldLabel>
            <input className={fieldClass} type="time" value={time} onChange={(event) => setTime(event.target.value)} />
          </div>
          <p className="text-sm text-[#6B6B6B]">Planning a meal doesn’t add ingredients to your shopping lists.</p>
        </div>
      )}
      <div className="mt-6 flex items-center justify-end gap-4">
        <TextCancelButton onClick={onClose} />
        <GreenPillButton onClick={save}>Add to meal</GreenPillButton>
      </div>
    </ShoppingModalShell>
  )
}
